import React, { useEffect, useState, useMemo, useCallback } from "react";
import TableCompo from "../Table/TableCompo";
import { toast } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import {
  Box,
  Typography,
  CardContent,
  Button,
  useTheme,
  useMediaQuery,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import {
  exportTransactionsRequest,
  fetchTransactionsRequest,
  verifyPaymentRequest,
} from "../../../redux/Transactions/TransactionsSlice";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { Stack } from "@mui/material";
import { subMonths } from "date-fns";

const formatDateTimeRange = (date, type) => {
  if (!date) return null;

  const d = new Date(date);

  if (type === "start") {
    d.setHours(0, 0, 0, 0);
    return d.toISOString();
  }

  if (type === "end") {
    d.setHours(23, 59, 59, 999);
    return d.toISOString();
  }
};

const VERIFY_EMAILS = [
  "tech@instalaxmi.com",
  "dayanand@instalaxmi.com",
  "vikrant@instalaxmi.com",
];

const reportColumnConfig = {
  transaction_details: {
    "68ff5d851eba1e7c09615c59": {
      "Total Value": false,
    },
  },
};

const TransactionDetails = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const dispatch = useDispatch();
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("SUCCESS");
  const [oldTransaction, setOldTransaction] = useState(false);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  // Applied only after "Get Details" (avoids double-fetch + unvalidated auto-fetch)
  const [appliedDateRange, setAppliedDateRange] = useState(null);
  const [pagination, setPagination] = useState({ page: 0, limit: 10 });
  const [splitRow, setSplitRow] = useState(null);

  const package_name = useSelector(
    (state) => state?.appConfig?.configData.package_name || "",
  );
  const jw = useSelector(
    (state) => state?.appConfig?.configData?.jeweller || "",
  );
  const decimal = useSelector(
    (state) => state?.appConfig?.configData?.decimalPrecision || 4,
  );
  const taxType = useSelector(
    (state) => state?.appConfig?.configData?.taxtype || "GST",
  );
  const sixMonthsAgo = useMemo(() => subMonths(new Date(), 6), []);

  const { transactions, total, loading } = useSelector(
    (state) => state.transactions,
  );
  const data = Array.isArray(transactions?.transactionData)
    ? transactions.transactionData
    : [];

  useEffect(() => {
    const payload = {
      status: statusFilter,
      search: { value: search },
      columns: [],
      order: [],
      page: pagination.page + 1,
      limit: pagination.limit,
      oldTransaction: oldTransaction,
    };
    if (appliedDateRange?.start && appliedDateRange?.end) {
      payload.startDate = formatDateTimeRange(appliedDateRange.start, "start");
      payload.endDate = formatDateTimeRange(appliedDateRange.end, "end");
    }
    dispatch(fetchTransactionsRequest(payload));
  }, [
    dispatch,
    pagination,
    search,
    statusFilter,
    appliedDateRange,
    oldTransaction,
  ]);

  const handleGetDetails = () => {
    if (!startDate || !endDate) {
      toast.error("Please select both start and end dates.");
      return;
    }
    if (startDate > endDate) {
      toast.error("Start Date cannot be after End Date");
      return;
    }
    if (!oldTransaction && startDate < sixMonthsAgo) {
      toast.error(
        "For current transactions, start date must be within last 6 months.",
      );
      return;
    }
    if (oldTransaction && startDate >= sixMonthsAgo) {
      toast.error(
        "For old transactions, start date must be older than 6 months.",
      );
      return;
    }

    setPagination((prev) => (prev.page === 0 ? prev : { ...prev, page: 0 }));
    setAppliedDateRange({ start: startDate, end: endDate });
  };

  const handleOldTransactionToggle = () => {
    setOldTransaction((prev) => !prev);
    setStartDate(null);
    setEndDate(null);
    setAppliedDateRange(null);
  };

  const handleTableChange = useCallback((action, tableState) => {
    switch (action) {
      case "changePage":
        setPagination((prev) => ({ ...prev, page: tableState.page }));
        break;
      case "changeRowsPerPage":
        setPagination({ page: 0, limit: tableState.rowsPerPage });
        break;
      case "search":
        setSearch(tableState.searchText || "");
        setPagination((prev) =>
          prev.page === 0 ? prev : { ...prev, page: 0 },
        );
        break;
      default:
        break;
    }
  }, []);

  const handleExport = () => {
    dispatch(
      exportTransactionsRequest({
        startDate: formatDateTimeRange(
          appliedDateRange?.start || startDate,
          "start",
        ),
        endDate: formatDateTimeRange(appliedDateRange?.end || endDate, "end"),
        status: statusFilter,
      }),
    );
  };

  const verifyStatus = useCallback(
    (payload) => {
      dispatch(
        verifyPaymentRequest({
          order_id: payload._id,
          onSuccess: () => {
            dispatch(
              fetchTransactionsRequest({
                status: statusFilter,
                search: { value: search },
                columns: [],
                order: [],
                page: pagination.page + 1,
                limit: pagination.limit,
                oldTransaction,
                ...(appliedDateRange?.start && appliedDateRange?.end
                  ? {
                      startDate: formatDateTimeRange(
                        appliedDateRange.start,
                        "start",
                      ),
                      endDate: formatDateTimeRange(
                        appliedDateRange.end,
                        "end",
                      ),
                    }
                  : {}),
              }),
            );
          },
        }),
      );
    },
    [
      dispatch,
      statusFilter,
      search,
      pagination,
      oldTransaction,
      appliedDateRange,
    ],
  );

  const columns = useMemo(() => {
    const showTotalValue =
      !!reportColumnConfig.transaction_details[jw || ""]?.["Total Value"];

    return [
      {
        field: "createdAt",
        headerName: "Date",
        render: (val, row) => {
          return row?.createdAt
            ? new Date(row.createdAt).toLocaleString("en-IN", {
                day: "2-digit",
                month: "short",
                year: "numeric",
                hour: "2-digit",
                minute: "2-digit",
                hour12: true,
              })
            : "N.A";
        },
      },
      {
        field: "customer",
        headerName: " Name",
        render: (val, row) =>
          row.customer
            ? `${row.customer.firstName || ""} ${row.customer.middleName || ""} ${row.customer.lastName || ""}`
            : "N.A",
      },
      {
        field: "phone",
        headerName: "Phone",
        render: (val, row) =>
          row.customer ? `${row.customer.phone || ""}` : "N.A",
      },
      {
        field: "paymentType",
        headerName: "Payment Mode",
        render: (val, row) => (row ? `${row.paymentType || ""}` : "N.A"),
      },
      {
        field: "orderType",
        headerName: "Order Type",
        render: (val, row) => (row ? `${row.order_type || ""}` : "N.A"),
      },
      {
        field: "order_id",
        headerName: "Order ID",
        render: (val, row) => {
          const cashfreeOrderId = row?.cashfree_orderid?.trim?.();
          if (cashfreeOrderId) return cashfreeOrderId;
          return row?.order_id && row.order_id !== "NA" ? row.order_id : "N.A";
        },
      },
      {
        field: "invoiceNumber",
        headerName: "Invoice Number",
        render: (val, row) => {
          const orderType = row?.order_type?.toLowerCase();

          const invoiceNumber = ["sell", "redeem"].includes(orderType)
            ? row?.order_id
            : row?.invoiceNumber;
          const transactions =
            row?.transactions || row?.Transaction || row?.transactionDetails;

          const handleInvoiceClick = () => {
            if (Array.isArray(transactions) && transactions.length > 0) {
              const transactionId = transactions[0]?._id;
              if (transactionId) {
                const invoiceUrl = `${window.location.origin}/invoice/${transactionId}`;
                window.open(invoiceUrl, "_blank");
              }
            }
          };

          return invoiceNumber ? (
            <span
              onClick={handleInvoiceClick}
              style={{
                backgroundColor: "#4CAF50",
                color: "white",
                padding: "2px 6px",
                borderRadius: "3px",
                fontSize: "12px",
                display: "inline-block",
                fontWeight: "400",
                lineHeight: "1.5",
                cursor: "pointer",
                textDecoration: "underline",
              }}
            >
              {invoiceNumber}
            </span>
          ) : (
            "N.A"
          );
        },
      },
      {
        field: "bullion",
        headerName: "Metal",
        render: (val, row) => {
          const bullion = row?.bullion;
          if (!bullion && row?.metalName === "Money") return "Money";
          if (!bullion && row?.metalName === "Product") return "Product";
          if (!bullion && row?.metalName === "Jewellery") return "Jewellery";
          if (
            !bullion &&
            row?.metalName !== "Money" &&
            row?.metalName !== "Product" &&
            row?.metalName !== "Jewellery"
          )
            return "N.A";

          const metalName = bullion.metalName || "";
          const karat = bullion.karat ? `${bullion.karat}K` : "";
          const purity = bullion.purity?.toString() || "";

          const combined = [metalName, karat, purity].filter(Boolean).join(" ");

          return combined || row.metalName;
        },
      },
      {
        field: "weight",
        headerName: "Weight",
        render: (val, row) => {
          return row?.weight != null
            ? Number(row.weight).toFixed(decimal)
            : "N.A";
        },
      },
      {
        field: "metalRate",
        headerName: "Metal Rate",
        render: (val, row) => {
          const tx = Array.isArray(row.transactions)
            ? row.transactions[0]
            : null;
          return tx ? (tx.rate === 0 ? "N.A" : tx.rate) : "N.A";
        },
      },
      ...(showTotalValue
        ? [
            {
              field: "totalValue",
              headerName: "Total Value",
              render: (val, row) => {
                const tx = Array.isArray(row.transactions)
                  ? row.transactions[0]
                  : null;

                if (!tx) return "N.A";
                return (tx?.final_amount).toFixed(2);
              },
            },
          ]
        : []),
      {
        field: "cgst",
        headerName: "CGST",
        render: (val, row) => {
          const tx = Array.isArray(row.transactions)
            ? row.transactions[0]
            : null;
          if (!tx) return "N.A";
          if (tx?.igst && tx?.igst > 0) return "N/A";
          if (tx.cgst === 0) return "N.A";

          return tx.cgst.toFixed(2);
        },
      },
      {
        field: "sgst",
        headerName: "SGST",
        render: (val, row) => {
          const tx = Array.isArray(row.transactions)
            ? row.transactions[0]
            : null;

          if (tx?.igst && tx?.igst > 0) return "N/A";

          if (!tx) return "N.A";
          if (!tx.sgst || tx.sgst === 0) return "N.A";

          return tx.sgst.toFixed(2);
        },
      },
      {
        field: "igst",
        headerName: "IGST",
        render: (val, row) => {
          const tx = row?.transactions?.[0];

          if (!tx) return "N.A";
          if (tx?.igst && tx?.igst > 0) return tx?.igst.toFixed(2);
          if (!tx.total_tax || tx.total_tax === 0) return "N.A";
          if (!tx.igst || tx.igst === 0) return "N.A";

          return tx.igst.toFixed(2);
        },
      },
      {
        field: "tax",
        headerName: taxType === "VAT" ? "Total TAX" : "Total GST",
        render: (val, row) => {
          const tx = Array.isArray(row.transactions)
            ? row.transactions[0]
            : null;

          if (!tx) return "N.A";
          if (tx.igst === undefined || tx.igst === null) return "N.A";
          return (
            parseFloat(tx?.total_tax || 0) - parseFloat(tx?.cess || 0)
          ).toFixed(2);
        },
      },
      {
        field: "cess",
        headerName: "CESS",
        render: (val, row) => {
          const tx = Array.isArray(row.transactions)
            ? row.transactions[0]
            : null;

          if (!tx) return "N.A";
          if (tx.cess === undefined || tx.cess === null) return "N.A";

          return (tx?.cess).toFixed(2);
        },
      },
      {
        field: "total_tax",
        headerName: taxType === "VAT" ? "Total TAX" : "Total GST",
        render: (val, row) => {
          const tx = Array.isArray(row.transactions)
            ? row.transactions[0]
            : null;

          if (!tx) return "N.A";
          if (!tx.total_tax || tx.total_tax === 0) return "N.A";

          return tx.total_tax.toFixed(2);
        },
      },
      {
        field: "amount",
        headerName: "Amount",
        render: (val, row) => {
          return row?.amount != null
            ? Number(row.amount).toFixed(2)
            : "N.A";
        },
      },
      {
        field: "status",
        headerName: "Status",
        render: (val, row) => {
          const status = row.status?.toUpperCase();
          let color;

          switch (status) {
            case "SUCCESS":
              color = "green";
              break;
            case "PENDING":
              color = "blue";
              break;
            default:
              color = "red";
              break;
          }

          const handleStatusClick = () => {
            if (status === "SUCCESS") {
              const transactions =
                row?.transactions ||
                row?.Transaction ||
                row?.transactionDetails;

              if (Array.isArray(transactions) && transactions.length > 0) {
                const transactionId = transactions[0]?._id;
                if (transactionId) {
                  const invoiceUrl = `${window.location.origin}/invoice/${transactionId}`;
                  window.open(invoiceUrl, "_blank");
                }
              }
            }
          };

          return (
            <span
              style={{
                color,
                fontWeight: "bold",
                cursor: status === "SUCCESS" ? "pointer" : "default",
                textDecoration: status === "SUCCESS" ? "underline" : "none",
              }}
              onClick={handleStatusClick}
            >
              {status}
            </span>
          );
        },
      },
      {
        field: "paymentSplit",
        headerName: "Split",
        render: (_value, row) => {
          const split = row?.paymentSplit;
          if (!split?.vendors?.length) return "—";
          return (
            <Button
              size="small"
              variant="outlined"
              onClick={() => setSplitRow(row)}
              sx={{ textTransform: "none" }}
            >
              {split.status === "SETTLED" ? "Settled" : "Pending"}
            </Button>
          );
        },
      },
      {
        field: "_id",
        headerName: "Recheck Payment",
        render: (val, row) => {
          const status = row.status?.toUpperCase();
          const storedEmail = localStorage.getItem("userEmail");
          const isPending =
            status === "PENDING" &&
            storedEmail &&
            VERIFY_EMAILS.includes(storedEmail);

          return (
            <Button
              variant={isPending ? "outlined" : "contained"}
              size="small"
              onClick={() => isPending && verifyStatus(row)}
              disabled={!isPending}
              startIcon={!isPending ? <CheckCircleOutlineIcon /> : null}
              sx={{
                textTransform: "none",
                borderColor: isPending ? "blue" : "grey.400",
                color: isPending ? "blue" : "grey.500",
                "&.Mui-disabled": {
                  borderColor: "grey.400",
                  color: "grey.500",
                },
              }}
            >
              {isPending ? "Verify Payment" : status}
            </Button>
          );
        },
      },
    ];
  }, [taxType, decimal, jw, verifyStatus]);

  const filteredColumns = useMemo(() => {
    if (taxType === "VAT") {
      return columns.filter(
        (col) => !["cgst", "sgst", "igst"].includes(col.field),
      );
    }
    return columns.filter((col) => !["tax", "cess"].includes(col.field));
  }, [taxType, columns]);

  return (
    <Box sx={{ width: "100%", minHeight: "100vh", overflow: "auto", pb: 4 }}>
      <Box
        sx={{
          width: "100%",
          mt: 0,
          background: "#fff",
          borderRadius: "16px",
          boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
          pb: 3,
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            px: 2,
            pt: 2,
            pb: 1,
            flexWrap: "wrap",
            gap: 1,
          }}
        >
          <Typography fontWeight={600} fontSize={25} fontFamily="Aeonik">
            Transaction Details
          </Typography>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              px: 1.5,
              py: 0.5,
              border: "1px solid #E0E0E0",
              borderRadius: "12px",
              backgroundColor: oldTransaction ? "#F3E5F5" : "#FAFAFA",
              transition: "all 0.3s ease",
              boxShadow: oldTransaction
                ? "0 2px 8px rgba(156, 39, 176, 0.15)"
                : "0 1px 4px rgba(0,0,0,0.08)",
              "&:hover": {
                backgroundColor: oldTransaction ? "#EEDCF2" : "#F5F5F5",
              },
            }}
          >
            <Checkbox
              checked={oldTransaction}
              onChange={handleOldTransactionToggle}
              size="small"
              sx={{
                color: "#9C27B0",
                "&.Mui-checked": {
                  color: "#9C27B0",
                },
                padding: "6px",
              }}
            />

            <Box>
              <Typography
                sx={{
                  fontSize: 14,
                  fontWeight: 600,
                  color: "#2D2D2D",
                  lineHeight: 1.2,
                }}
              >
                Old Transactions
              </Typography>

              <Typography
                sx={{
                  fontSize: 11,
                  color: "#777",
                  mt: 0.2,
                }}
              >
                Show transactions older than 6 months
              </Typography>
            </Box>
          </Box>
        </Box>

        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <Box
            display="flex"
            flexDirection={isMobile ? "column" : "row"}
            gap={1.5}
            px={2}
            py={1}
          >
            <DatePicker
              label="Start Date"
              value={startDate}
              onChange={setStartDate}
              minDate={!oldTransaction ? sixMonthsAgo : undefined}
              maxDate={oldTransaction ? sixMonthsAgo : endDate || new Date()}
              slotProps={{
                textField: {
                  size: "small",
                  sx: {
                    minWidth: 160,
                    "& .MuiInputBase-root": { height: 36, fontSize: 13 },
                    "& .MuiInputLabel-root": { fontSize: 13 },
                  },
                },
              }}
            />

            <DatePicker
              label="End Date"
              value={endDate}
              onChange={setEndDate}
              minDate={!oldTransaction ? startDate || sixMonthsAgo : undefined}
              maxDate={oldTransaction ? sixMonthsAgo : new Date()}
              slotProps={{
                textField: {
                  size: "small",
                  sx: {
                    minWidth: 160,
                    "& .MuiInputBase-root": { height: 36, fontSize: 13 },
                    "& .MuiInputLabel-root": { fontSize: 13 },
                  },
                },
              }}
            />

            <Button
              variant="outlined"
              fullWidth={isMobile}
              sx={{
                height: 36,
                minWidth: 120,
                fontSize: 13,
                textTransform: "none",
                px: 2,
                whiteSpace: "nowrap",
              }}
              onClick={handleGetDetails}
            >
              Get Details
            </Button>
          </Box>
        </LocalizationProvider>

        <Box
          sx={{
            display: "flex",
            gap: 1,
            px: 2,
            pb: 1,
            alignItems: "center",
            flexWrap: "wrap",
          }}
        >
          <Button
            variant="contained"
            size="small"
            onClick={() => {
              setStatusFilter("SUCCESS");
              setPagination((prev) =>
                prev.page === 0 ? prev : { ...prev, page: 0 },
              );
            }}
            sx={{
              backgroundColor:
                statusFilter === "SUCCESS" ? "#4CAF50" : "#E0E0E0",
              color: statusFilter === "SUCCESS" ? "white" : "black",
              textTransform: "none",
              height: 32,
              fontSize: 12,
              px: 2,
              boxShadow: "none",
            }}
          >
            Success
          </Button>

          <Button
            variant="contained"
            size="small"
            onClick={() => {
              setStatusFilter("PENDING");
              setPagination((prev) =>
                prev.page === 0 ? prev : { ...prev, page: 0 },
              );
            }}
            sx={{
              backgroundColor:
                statusFilter === "PENDING" ? "#2196F3" : "#E0E0E0",
              color: statusFilter === "PENDING" ? "white" : "black",
              textTransform: "none",
              height: 32,
              fontSize: 12,
              px: 2,
              boxShadow: "none",
            }}
          >
            Pending
          </Button>

          <Button
            variant="contained"
            size="small"
            onClick={() => {
              setStatusFilter("CANCELLED");
              setPagination((prev) =>
                prev.page === 0 ? prev : { ...prev, page: 0 },
              );
            }}
            sx={{
              backgroundColor:
                statusFilter === "CANCELLED" ? "#f44336" : "#E0E0E0",
              color: statusFilter === "CANCELLED" ? "white" : "black",
              textTransform: "none",
              height: 32,
              fontSize: 12,
              px: 2,
              boxShadow: "none",
            }}
          >
            Cancel
          </Button>

          <Typography fontSize={12} color="gray">
            Click buttons to filter data.
          </Typography>
        </Box>
        <CardContent sx={{ width: "100%", overflowX: "auto" }}>
          <Box sx={{ width: "100%", overflow: "auto" }}>
            <TableCompo
              columns={filteredColumns}
              data={data}
              totalCount={total}
              onTableChange={handleTableChange}
              page={pagination.page}
              limit={pagination.limit}
              loading={loading}
              searchDebounceMs={400}
              extraButtons={
                <Stack direction="row" spacing={2}>
                  <>
                    <Button
                      className="action-btn"
                      size="small"
                      variant="outlined"
                      onClick={handleExport}
                    >
                      Extract CSV
                    </Button>

                    <Button
                      className="action-btn"
                      size="small"
                      variant="outlined"
                      component="a"
                      href={`${
                        process.env.REACT_APP_API_BASE_URL
                      }/static_data/${package_name}/transactions.xlsx?nocache=${Date.now()}`}
                      download="transactions.xlsx"
                    >
                      Download
                    </Button>
                  </>
                </Stack>
              }
            />
          </Box>
        </CardContent>
      </Box>
      <SplitDetailsDialog row={splitRow} onClose={() => setSplitRow(null)} />
    </Box>
  );
};

const formatInr = (value) =>
  Number(value || 0).toLocaleString("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 2,
  });

const SplitDetailsDialog = ({ row, onClose }) => {
  const split = row?.paymentSplit;
  return (
    <Dialog open={Boolean(split)} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>Payment split</DialogTitle>
      <DialogContent>
        {split ? (
          <Box>
            <Typography variant="body2" sx={{ mb: 1 }}>
              Order {row?.order_id || "N.A"} · {split.status}
            </Typography>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                gap: 1,
                mb: 2,
              }}
            >
              <Typography variant="body2">
                Booked amount: {formatInr(split.bookedAmount)}
              </Typography>
              <Typography variant="body2">
                Jeweller account:{" "}
                {formatInr(
                  split.jewellerAmount ??
                    Number(split.bookedAmount || 0) -
                      (split.vendors || []).reduce(
                        (sum, vendor) => sum + Number(vendor.amount || 0),
                        0,
                      ),
                )}
              </Typography>
              <Typography variant="body2">
                Live total with GST: {formatInr(split.totalAmount)}
              </Typography>
              <Typography variant="body2">Tax: {formatInr(split.tax)}</Typography>
              <Typography variant="body2">
                Margin with GST: {formatInr(split.marginWithGst)}
              </Typography>
              <Typography variant="body2">
                Margin without GST: {formatInr(split.marginWithoutGst)}
              </Typography>
              <Typography variant="body2">
                Live rate with GST: {formatInr(split.liveRate?.liveRateWithGst)}
              </Typography>
              <Typography variant="body2">
                Live rate without GST:{" "}
                {formatInr(split.liveRate?.liveRateWithoutGst)}
              </Typography>
            </Box>
            {(split.vendors || []).map((vendor) => (
              <Box
                key={`${vendor.cashfreeVendorId}-${vendor.amount}`}
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  gap: 2,
                  py: 1,
                  borderTop: "1px solid #eee",
                }}
              >
                <Box>
                  <Typography fontWeight={600}>{vendor.name}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {vendor.cashfreeVendorId}
                  </Typography>
                </Box>
                <Box sx={{ textAlign: "right" }}>
                  <Typography>{formatInr(vendor.amount)}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {vendor.booking_percentage}%
                  </Typography>
                </Box>
              </Box>
            ))}
          </Box>
        ) : null}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default TransactionDetails;
